import {
  Fragment,
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type MouseEvent,
  type Ref,
} from 'react';
import {
  Bookmark,
  ChartNoAxesColumn,
  ChevronLeft,
  ChevronRight,
  Ellipsis,
  FileText,
  Forward,
  Globe,
  Heart,
  Loader2,
  MessageCircle,
  MessageSquare,
  Repeat2,
  Send,
  Share,
  ThumbsUp,
} from 'lucide-react';
import { SocialPlatform } from '@mg/shared';
import { Avatar } from '../../components/ui/Avatar';
import { cn } from '../../lib/cn';
import { usePdfPage } from '../../lib/usePdfPage';
import { useThumbnailSrc } from '../../lib/useProxyImage';
import { IMAGE_ARROWS, imageArrowToFocus, keptSlides } from './imageCarousel';
import { PreviewImageGrid } from './PreviewImageGrid';
import { THREAD_PREVIEW_MAX_CELLS } from './threadPreviewPosts';
import {
  PAGE_ARROWS,
  arrowShown,
  arrowToFocus,
  clampPage,
  frameShape,
  frameShapeOf,
  mountedPages,
  pageAfter,
  pageableCount,
  pdfPageUrl,
  retryPlan,
  type PageArrow,
} from './pdfPages';
import { PLATFORM_META } from './platforms';
import {
  collapsedBody,
  hashtagRuns,
  previewIdentity,
  PREVIEW_STYLE,
  SHOW_LESS,
  type PreviewAccount,
  type PreviewPlatform,
} from './previewRules';

type IconComponent = ComponentType<{ className?: string }>;

interface Action {
  Icon: IconComponent;
  /** Printed beside the icon on the platforms that label their actions (LinkedIn, Facebook). */
  label?: string;
}

interface ActionSet {
  lead: Action[];
  trail?: Action[];
  /**
   * When the labels print: only once the card is wide enough for the whole labelled row. In the
   * composer's side column the card can be far narrower than the 400px it is drawn at, and there a
   * row of icons beats a row whose last action is cut off. In rem, like the row it measures, so a
   * larger browser text size moves the threshold with it.
   */
  labelClass?: string;
}

/**
 * Each platform's row of actions under a post, as decoration only: no buttons, no counts. Who
 * liked a post that has not gone out yet is not something to make up.
 */
const ACTIONS: Record<PreviewPlatform, ActionSet> = {
  [SocialPlatform.LINKEDIN]: {
    lead: [
      { Icon: ThumbsUp, label: 'Like' },
      { Icon: MessageSquare, label: 'Comment' },
      { Icon: Repeat2, label: 'Repost' },
      { Icon: Send, label: 'Send' },
    ],
    labelClass: 'hidden [@container(min-width:18.5rem)]:inline',
  },
  [SocialPlatform.FACEBOOK]: {
    lead: [
      { Icon: ThumbsUp, label: 'Like' },
      { Icon: MessageCircle, label: 'Comment' },
      { Icon: Forward, label: 'Share' },
    ],
    labelClass: 'hidden [@container(min-width:14.25rem)]:inline',
  },
  [SocialPlatform.INSTAGRAM]: {
    lead: [{ Icon: Heart }, { Icon: MessageCircle }, { Icon: Repeat2 }, { Icon: Send }],
    trail: [{ Icon: Bookmark }],
  },
  [SocialPlatform.THREADS]: {
    lead: [{ Icon: Heart }, { Icon: MessageCircle }, { Icon: Repeat2 }, { Icon: Send }],
  },
  [SocialPlatform.X]: {
    lead: [{ Icon: MessageCircle }, { Icon: Repeat2 }, { Icon: Heart }, { Icon: ChartNoAxesColumn }],
    trail: [{ Icon: Bookmark }, { Icon: Share }],
  },
};

/** One picture of a post that holds several, by the key of its block, and where to load it from. */
export interface PostImage {
  key: string;
  /** Its small copy, where one was made (else the original): what a grid cell loads. */
  url: string | null;
  /**
   * The original: what a slide of Instagram's carousel loads, being drawn the whole card wide - as
   * the one picture of a post is - where the 300px small copy would be blurred. Absent: `url`.
   */
  full?: string | null;
}

export interface PlatformPreviewProps {
  platform: PreviewPlatform;
  /** The connected account the post goes out as. */
  account: PreviewAccount;
  /** The post's text, without the hashtags that end it. */
  text: string;
  /** The hashtags that end the post; set on a line of their own. */
  hashtags: string[];
  /**
   * An image URL ready for an <img> (already past the auth proxy), or null for a text-only post.
   * For a PDF, its first page.
   */
  imageUrl: string | null;
  /**
   * A PDF the post carries, which LinkedIn shows as a carousel of its pages under this title. Drawn
   * on LinkedIn only - no other destination can publish one, and the composer will not let one go.
   * With the PDF's `assetId` and a page count, the reader can page through it: the pages after the
   * first are fetched from the server, each one page ahead of the reader, once the reader reaches
   * for the document. (Required, null when there is none, so that a caller which drops it does
   * not compile.)
   */
  pdf?: { title: string; pageCount: number | null; assetId: string | null } | null;
  /**
   * Every picture of the post, in order, each as addresses still to be loaded (a cell loads its
   * own). With two or more the post is drawn as the grid the thread preview draws - at most four
   * cells, the fourth saying how many more there are ("+2"), each loading its picture's small copy -
   * in place of `imageUrl`; on Instagram, which always shows several pictures as a carousel, as that
   * carousel instead, one picture at a time, each loading its original. With one, or none, the
   * picture is `imageUrl`, as it always was. Never drawn beside a PDF.
   */
  images?: PostImage[];
  /**
   * The workspace's logo, as an image URL ready for an <img> (already past the auth proxy): drawn in
   * the account's avatar circle. Null or absent: the account's initials, as before.
   */
  avatarUrl?: string | null;
  /** Start with the whole post showing. Also how the expanded state is rendered in tests. */
  defaultExpanded?: boolean;
}

/**
 * A post as it will look in the platform's feed: the account, the text and hashtags, the image and
 * the platform's action row. LinkedIn, Facebook and Instagram cut a long post the way their feeds
 * do and offer the rest behind a real button; Threads and X show it all. Nothing is invented: no
 * counts, and the time is "Just now" / "now".
 */
export function PlatformPreview({
  platform,
  account,
  text,
  hashtags,
  imageUrl,
  pdf = null,
  images = [],
  avatarUrl = null,
  defaultExpanded = false,
}: PlatformPreviewProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  // "…see more" and "show less" are different buttons in different places, so the one pressed
  // leaves the page and focus has to go somewhere. Opening sends it to the post's text, where the
  // reader already is, without scrolling: Space and the arrow keys read on from there, so does a
  // screen reader, and "show less" is the next Tab stop. (On "show less" itself, at the far end of
  // the post, the next Space would close what was just opened.) Closing sends it back to
  // "…see more" and scrolls there, since a long post can close far above the reader.
  const textRef = useRef<HTMLParagraphElement>(null);
  const seeMoreRef = useRef<HTMLButtonElement>(null);
  const refocus = useRef(false);
  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    if (expanded) textRef.current?.focus({ preventScroll: true });
    else seeMoreRef.current?.focus();
  }, [expanded]);
  const toggle = (next: boolean) => (e: MouseEvent<HTMLButtonElement>) => {
    // Opening moves focus only when the button had it. Closing always does: Safari and Firefox on
    // macOS never focus a clicked button, and their readers would be left below the closed post.
    refocus.current = !next || document.activeElement === e.currentTarget;
    setExpanded(next);
  };
  const style = PREVIEW_STYLE[platform];
  const meta = PLATFORM_META[platform];
  const identity = previewIdentity(platform, account);
  // A post of nothing but hashtags is set as the text itself: there is nothing for them to follow
  // onto a line of their own, and Instagram still leads it with the username.
  const tagsOnly = !text && hashtags.length > 0;
  const bodyText = tagsOnly ? hashtags.join(' ') : text;
  const bodyTags = tagsOnly ? [] : hashtags;
  const body = collapsedBody(bodyText, bodyTags, style.collapseAt);
  // Everything shows when the post was never cut, or once the user has opened it.
  const open = expanded || !body.truncated;
  const shownText = open ? bodyText : body.shown;
  const instagram = platform === SocialPlatform.INSTAGRAM;
  const pad = instagram ? 'px-3' : 'px-4';

  const seeMore = !open && style.seeMore && (
    <>
      {/* Instagram's control says just "more", after an ellipsis on the text itself. */}
      {instagram ? '… ' : ' '}
      <PreviewToggle buttonRef={seeMoreRef} expanded={false} onClick={toggle(true)}>
        {style.seeMore}
      </PreviewToggle>
    </>
  );
  const hashtagLine = open && bodyTags.length > 0 && (
    <p className={cn(pad, 'break-words pb-2 text-sm', meta.link)}>{bodyTags.join(' ')}</p>
  );
  const showLess = open && body.truncated && (
    <div className={cn(pad, 'pb-2 text-sm')}>
      <PreviewToggle expanded onClick={toggle(false)}>
        {SHOW_LESS}
      </PreviewToggle>
    </div>
  );
  // A PDF is LinkedIn's alone: anywhere else no picture at all, never its first page passed off as a
  // photo post (the composer already says that destination can't take it).
  const image = pdf ? (
    platform === SocialPlatform.LINKEDIN ? (
      <DocumentPreview
        // Another PDF starts again from its first page.
        key={pdf.assetId ?? ''}
        title={pdf.title}
        pageCount={pdf.pageCount}
        pageUrl={imageUrl}
        assetId={pdf.assetId}
      />
    ) : null
  ) : images.length >= 2 ? (
    instagram ? (
      // Instagram shows several pictures as a carousel, always: one at a time, every one of them.
      <InstagramCarousel images={images} />
    ) : (
      // Several pictures elsewhere: the first four, the last of them counting the rest. One rule on
      // every other platform, and the same grid as a thread post's - whose cells, each a part of the
      // card, load the small copies, never the originals.
      <PreviewImageGrid
        pictures={images
          .slice(0, THREAD_PREVIEW_MAX_CELLS)
          .map(({ key, url }) => ({ key, url, video: false }))}
        more={Math.max(0, images.length - THREAD_PREVIEW_MAX_CELLS)}
      />
    )
  ) : imageUrl ? (
    <img src={imageUrl} alt="" className="block aspect-square w-full object-cover" />
  ) : null;

  return (
    <article
      aria-label={`${meta.label} post preview`}
      // A size container: nothing inside (a long account name, the action row) can widen the card
      // past its column, and the action row reads the card's width to decide on labels.
      // theme-light: the card copies the platform's own light feed, and its link colours are tuned
      // for white, so it stays light in the dark theme too (no dark: class anywhere inside it).
      className="theme-light mx-auto w-full max-w-[25rem] overflow-hidden rounded-lg border border-line bg-card text-left [container-type:inline-size]"
    >
      <PreviewHeader
        platform={platform}
        name={identity.name}
        handle={identity.handle}
        avatarUrl={avatarUrl}
      />

      {instagram ? (
        <>
          {image}
          <ActionRow platform={platform} />
          {(shownText || seeMore) && (
            <p
              ref={textRef}
              tabIndex={-1}
              className="whitespace-pre-wrap break-words px-3 pb-1 text-sm text-ink focus-visible:ring-inset"
            >
              <span className="font-semibold">{identity.name}</span> <PostText text={shownText} linkClass={meta.link} />
              {seeMore}
            </p>
          )}
          {hashtagLine}
          {showLess}
          <p className="px-3 pb-3 text-xs text-ink-muted">{style.timeLabel}</p>
        </>
      ) : (
        <>
          {(shownText || seeMore) && (
            <p
              ref={textRef}
              tabIndex={-1}
              className="whitespace-pre-wrap break-words px-4 pb-2 text-sm text-ink focus-visible:ring-inset"
            >
              <PostText text={shownText} linkClass={meta.link} />
              {seeMore}
            </p>
          )}
          {hashtagLine}
          {showLess}
          {image}
          <ActionRow platform={platform} />
        </>
      )}
    </article>
  );
}

/**
 * The space LinkedIn leaves between two pages while one slides out and the next slides in: 32px at
 * full size, in rem like the card, so it shrinks with it when index.css scales the UI to 80%.
 */
const PAGE_GAP_REM = 2;

/**
 * How a PDF's page - and a picture of Instagram's carousel, which moves the same way - slides:
 * LinkedIn's own timing, 0.2s ease-out, and not at all for a reader who asked for less motion.
 */
const SLIDE_MOTION = 'transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none';

/** Where a slide `offset` places from the one showing sits: a frame's width and a gap per place. */
const slideTransform = (offset: number) =>
  `translateX(calc(${offset * 100}% + ${offset * PAGE_GAP_REM}rem))`;

/**
 * A PDF as LinkedIn's feed shows it: the title above the document, a page at the document's own
 * shape (not cropped square - LinkedIn shows a document as it is), and where you are in it.
 *
 * Paging follows LinkedIn's own document viewer: a round arrow half-way down each side of the page,
 * taken away - not greyed out - at the end it cannot go past, and pages that slide sideways. The
 * first page is the picture drawn at upload (`pageUrl`); the others are fetched from the server,
 * the next one ahead of the reader so that a click has a page to slide in.
 */
export function DocumentPreview({
  title,
  pageCount,
  pageUrl,
  assetId,
  defaultPage = 1,
}: {
  title: string;
  pageCount: number | null;
  /** The first page, ready for an <img>; null while it loads, or when it was never drawn. */
  pageUrl: string | null;
  /** The PDF's asset. Without it, or without a page count, there is only the first page to show. */
  assetId: string | null;
  /** The page to open on. Also how a later page is rendered in tests. */
  defaultPage?: number;
}) {
  const count = pageableCount(pageCount, assetId);
  const [turnedTo, setTurnedTo] = useState(defaultPage);
  const page = clampPage(turnedTo, count);
  // Nothing is fetched for a preview that is merely open: the pages either side of the one showing
  // are asked for once the reader reaches for the document (the pointer over it, focus on an arrow)
  // - which comes before any click - or is already past its first page.
  const [reached, setReached] = useState(false);
  const reach = () => setReached(true);
  // The frame keeps one shape for the whole PDF, as LinkedIn's viewer does, so that neither it nor
  // the arrows move as the pages turn: the shape of the first page's picture. It is remembered
  // with the picture it was read from - one that has since been replaced or withdrawn no longer
  // speaks for this document. A later page's stands in only while the first has none.
  const [firstShape, setFirstShape] = useState<string | null>(null);
  const [firstShapeOf, setFirstShapeOf] = useState<string | null>(null);
  const [standIn, setStandIn] = useState<string | null>(null);
  const takeFirstShape = (img: HTMLImageElement) => {
    setFirstShape(frameShape(img.naturalWidth, img.naturalHeight));
    setFirstShapeOf(img.getAttribute('src'));
  };
  const takeStandIn = (img: HTMLImageElement) =>
    setStandIn((held) => held ?? frameShape(img.naturalWidth, img.naturalHeight));
  const shape = frameShapeOf(firstShapeOf === pageUrl ? firstShape : null, standIn);
  // An arrow is taken away at the end it leads to. When it was pressed with focus on it (a keyboard,
  // or a mouse in a browser that focuses a clicked button), that focus moves to the arrow that is
  // left instead of being dropped; anywhere else it stays where it is, for the next press.
  const arrowRefs = useRef<Partial<Record<PageArrow['side'], HTMLButtonElement | null>>>({});
  const pressed = useRef<PageArrow | null>(null);
  useEffect(() => {
    const arrow = pressed.current;
    pressed.current = null;
    const next = arrow && arrowToFocus(arrow, page, count);
    if (next) arrowRefs.current[next.side]?.focus({ preventScroll: true });
  }, [page, count]);
  const press = (arrow: PageArrow) => (e: MouseEvent<HTMLButtonElement>) => {
    pressed.current = document.activeElement === e.currentTarget ? arrow : null;
    setReached(true);
    setTurnedTo(pageAfter(arrow, page, count));
  };

  return (
    // theme-light: a LinkedIn document stays light like the card it sits in.
    <figure className="theme-light border-y border-line bg-page">
      <figcaption className="flex items-center justify-between gap-3 px-4 py-2 text-xs">
        <span className="min-w-0 truncate font-semibold text-ink">{title}</span>
        {pageCount != null && (
          <span className="shrink-0 text-ink-muted">
            {pageCount} page{pageCount === 1 ? '' : 's'}
          </span>
        )}
      </figcaption>
      <div
        className="relative overflow-hidden bg-card"
        style={{ aspectRatio: shape }}
        onPointerEnter={reach}
        onFocus={reach}
      >
        {mountedPages(page, count, reached || page > 1).map((n) => (
          <DocumentPage
            key={n}
            page={n}
            pageCount={pageCount}
            offset={n - page}
            ready={n === 1 ? pageUrl : null}
            url={n > 1 && assetId ? pdfPageUrl(assetId, n) : null}
            onPicture={n === 1 ? takeFirstShape : n === page ? takeStandIn : undefined}
          />
        ))}
        {PAGE_ARROWS.filter((arrow) => arrowShown(arrow, page, count)).map((arrow) => (
          <PageArrowButton
            key={arrow.side}
            ref={(el) => {
              arrowRefs.current[arrow.side] = el;
            }}
            arrow={arrow}
            onClick={press(arrow)}
          />
        ))}
      </div>
      {pageCount != null && (
        // Read out as the page changes; the slash is for the eye only.
        <p
          aria-live="polite"
          aria-atomic="true"
          className="px-4 py-1.5 text-right text-xs tabular-nums text-ink-muted"
        >
          <span aria-hidden="true">
            {page} / {pageCount}
          </span>
          <span className="sr-only">
            Page {page} of {pageCount}
          </span>
        </p>
      )}
    </figure>
  );
}

/**
 * One page in the frame. `offset` is how many pages it sits from the one showing: 0 fills the
 * frame, -1 and 1 wait just outside it, already loading, and slide in when the reader steps to them.
 */
function DocumentPage({
  page,
  pageCount,
  offset,
  ready,
  url,
  onPicture,
}: {
  page: number;
  pageCount: number | null;
  offset: number;
  /** A picture already in hand (the first page), used instead of fetching one. */
  ready: string | null;
  /** Where to fetch this page's picture; null when there is nothing to fetch. */
  url: string | null;
  /** Told of this page's picture once it has loaded, when the frame may take its shape from it. */
  onPicture?: (img: HTMLImageElement) => void;
}) {
  const { src: fetchedSrc, failed, retry } = usePdfPage(url);
  const src = ready ?? fetchedSrc;
  const showing = offset === 0;
  const label = pageCount != null ? `Page ${page} of ${pageCount}` : `Page ${page}`;

  // A page that could not be loaded is asked for again while the reader is on it: at once when
  // it failed while it waited at the side, then after a moment, a few times and no more (see
  // retryPlan). "Right now" may have passed, and nothing else would ask again. The count is
  // state, not a ref, so that each ask arms the next wait by itself: an ask that is turned away
  // at once comes back without `failed` ever being seen to change.
  const wasShowing = useRef(showing);
  const [retries, setRetries] = useState(0);
  useEffect(() => {
    const arrived = showing && !wasShowing.current;
    wasShowing.current = showing;
    if (!showing) setRetries(0);
    const plan = retryPlan({ showing, arrived, failed, retries });
    if (plan.now) retry();
    if (plan.wait === null) return;
    const waiting = setTimeout(() => {
      setRetries((n) => n + 1);
      retry();
    }, plan.wait);
    return () => clearTimeout(waiting);
  }, [showing, failed, retries, retry]);

  // The picture may have finished loading before the frame wanted to hear of it (the page waited
  // at the side, loaded), so its `load` event alone is not enough to tell the frame.
  const imgRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    const img = imgRef.current;
    if (onPicture && img?.complete) onPicture(img);
  });

  return (
    <div
      // Only the page showing is there for a screen reader; its neighbours are out of sight.
      aria-hidden={showing ? undefined : true}
      className={cn(
        // LinkedIn's own timing: 0.2s ease-out, the page arriving brightening from a quarter.
        'absolute inset-0 flex items-center justify-center',
        SLIDE_MOTION,
        !showing && 'opacity-25',
      )}
      style={{ transform: slideTransform(offset) }}
    >
      {src ? (
        <img
          ref={imgRef}
          src={src}
          alt={label}
          draggable={false}
          onLoad={(e) => onPicture?.(e.currentTarget)}
          className="h-full w-full object-contain"
        />
      ) : failed ? (
        // Padded clear of the arrows, which sit at this height 16-48px in from each edge - and
        // words only: under a wide page in a narrow column there is no room for more.
        <p className="px-14 text-center text-xs text-ink-muted">
          Page {page} can’t be shown right now.
        </p>
      ) : url ? (
        <>
          <Loader2 className="h-6 w-6 animate-spin text-ink-ghost" aria-hidden />
          <span className="sr-only">Loading page {page}</span>
        </>
      ) : (
        // The first page before its picture arrives, or when it was never drawn.
        <FileText className="h-10 w-10 text-ink-ghost" aria-hidden />
      )}
    </div>
  );
}

const ARROW_ICON = { left: ChevronLeft, right: ChevronRight } as const;

/**
 * Previous / next, as LinkedIn's document viewer draws them: a 32px black circle at 80% with a white
 * chevron, 16px in from the edge and half-way down the page, turning solid black under the pointer
 * over half a second. Always there to be found (LinkedIn fades its own out when the pointer rests),
 * with a 44px target.
 */
const PageArrowButton = forwardRef<
  HTMLButtonElement,
  { arrow: PageArrow; onClick: (e: MouseEvent<HTMLButtonElement>) => void }
>(function PageArrowButton({ arrow, onClick }, ref) {
  const Icon = ARROW_ICON[arrow.side];
  return (
    <button
      ref={ref}
      type="button"
      aria-label={arrow.label}
      onClick={onClick}
      className={cn(
        "focus-ring absolute top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/80 text-white transition-colors duration-500 after:absolute after:-inset-1.5 after:content-[''] hover:bg-black",
        arrow.side === 'left' ? 'left-4' : 'right-4',
      )}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
});

/**
 * The dot that says which picture is showing: Instagram's own blue (the one its feed gives links),
 * picked out from the grey of the others. The dots are drawn with a background alone, which forced
 * colours (Windows High Contrast) paints over with the card's own colour, so each also names a
 * system colour, which survives: the text colour for the one showing, grey for the others.
 */
const DOT_SHOWING = cn(
  'bg-current forced-colors:[background-color:CanvasText]',
  PLATFORM_META[SocialPlatform.INSTAGRAM].link,
);
const DOT_OTHER = 'bg-line-dashed forced-colors:[background-color:GrayText]';

/**
 * Several pictures on Instagram, which always shows them as a carousel: one at a time, each a square
 * cropped like Instagram's single picture. The reader steps through them with the PDF carousel's
 * round arrows (DocumentPreview) - half-way down each side, taken away at the end they cannot go
 * past - and the pictures slide the way its pages do. No title bar: Instagram's carousel has none.
 * Under the pictures, Instagram's dots, one per picture, the one showing picked out.
 *
 * Each picture loads its own original - it is drawn the whole card wide, as the one picture of a
 * post is, and the small copy a grid cell loads would be blurred there: the one showing and the
 * next as the preview opens, one more with each step forward - never every picture up front
 * (keptSlides).
 */
export function InstagramCarousel({ images }: { images: PostImage[] }) {
  const count = images.length;
  const [turnedTo, setTurnedTo] = useState(1);
  // A picture taken out of the post can leave the reader past the end: then the last one shows.
  const slide = clampPage(turnedTo, count);
  const [furthest, setFurthest] = useState(1);
  // ...and from then on the reader is on that last one, and has been no further: pictures put in
  // after it are reached with the arrows, one fetched ahead of the reader as ever - not jumped to,
  // and not every one of them fetched at once.
  useEffect(() => {
    setTurnedTo((t) => clampPage(t, count));
    setFurthest((far) => Math.min(far, count));
  }, [count]);
  // As in DocumentPreview: focus on an arrow that is taken away at the end moves to the one left,
  // and focus anywhere else stays where it is.
  const arrowRefs = useRef<Partial<Record<PageArrow['side'], HTMLButtonElement | null>>>({});
  const pressed = useRef<PageArrow | null>(null);
  useEffect(() => {
    const arrow = pressed.current;
    pressed.current = null;
    const next = arrow && imageArrowToFocus(arrow, slide, count);
    if (next) arrowRefs.current[next.side]?.focus({ preventScroll: true });
  }, [slide, count]);
  const press = (arrow: PageArrow) => (e: MouseEvent<HTMLButtonElement>) => {
    pressed.current = document.activeElement === e.currentTarget ? arrow : null;
    const next = pageAfter(arrow, slide, count);
    setTurnedTo(next);
    setFurthest((far) => Math.max(far, next));
  };

  return (
    // theme-light: the pictures stay light like the card they sit in.
    <div className="theme-light">
      <div className="relative aspect-square overflow-hidden">
        {keptSlides(slide, furthest, count).map((n) => (
          <ImageSlide
            key={images[n - 1].key}
            url={images[n - 1].full ?? images[n - 1].url}
            offset={n - slide}
          />
        ))}
        {IMAGE_ARROWS.filter((arrow) => arrowShown(arrow, slide, count)).map((arrow) => (
          <PageArrowButton
            key={arrow.side}
            ref={(el) => {
              arrowRefs.current[arrow.side] = el;
            }}
            arrow={arrow}
            onClick={press(arrow)}
          />
        ))}
      </div>
      {/* Read out as the picture changes; the dots are for the eye only. */}
      <div aria-live="polite" aria-atomic="true" className="flex flex-wrap justify-center gap-1 pt-2.5">
        {images.map((image, i) => (
          <span
            key={image.key}
            aria-hidden="true"
            className={cn('h-1.5 w-1.5 rounded-full', i + 1 === slide ? DOT_SHOWING : DOT_OTHER)}
          />
        ))}
        <span className="sr-only">
          Image {slide} of {count}
        </span>
      </div>
    </div>
  );
}

/**
 * One picture of the carousel, in a component of its own so that it loads on its own, as a grid
 * cell does (PreviewImageGrid) - holding the cell's muted place until it has loaded, and only until
 * then: an original can be a PNG with clear parts, which show the card's white behind them, as they
 * do in the small copy and in the post as published (both are flattened onto white). `offset` is how
 * many places it sits from the picture showing: 0 fills the frame, the others wait outside it.
 */
function ImageSlide({ url, offset }: { url: string | null; offset: number }) {
  const src = useThumbnailSrc(url);
  // The picture that has finished loading, by its address: another picture arriving in its place
  // holds the muted place again until it has loaded in turn.
  const [loaded, setLoaded] = useState<string | null>(null);
  const showing = offset === 0;
  return (
    <div
      // Only the picture showing is there for a screen reader, as in the PDF carousel.
      aria-hidden={showing ? undefined : true}
      className={cn(
        'absolute inset-0',
        (!src || loaded !== src) && 'bg-chip',
        SLIDE_MOTION,
        !showing && 'opacity-25',
      )}
      style={{ transform: slideTransform(offset) }}
    >
      {src && (
        // A picture is moved with the arrows, not dragged off.
        <img
          src={src}
          alt=""
          draggable={false}
          onLoad={(e) => setLoaded(e.currentTarget.getAttribute('src'))}
          className="block h-full w-full object-cover"
        />
      )}
    </div>
  );
}

/**
 * The circle a preview avatar is drawn in, at the size Avatar draws the initials in at 'sm' and
 * 'md' (components/ui/Avatar.tsx `sizeClasses`): 32px and 40px at full size.
 */
const AVATAR_CIRCLE = { sm: 'h-8 w-8', md: 'h-10 w-10' } as const;

/**
 * The account's picture in a post preview, decorative: the name is printed beside it. The
 * workspace's logo when there is one (`avatarUrl`); otherwise - the account has no stored picture -
 * its initials.
 *
 * The logo is drawn whole in the circle, never cropped to fill it: a workspace with no Icon logo
 * hands over its Primary one, most often a wide wordmark, and cropped the way a profile photo is
 * (Avatar's `object-cover`, which a class cannot override: `cn` joins classes) it would show a few
 * letters from its middle. A square logo fills the circle either way. White behind it: around a
 * wide one, and behind a logo that is transparent.
 */
export function PreviewAvatar({
  name,
  size,
  avatarUrl,
}: {
  name: string;
  size: 'sm' | 'md';
  avatarUrl: string | null | undefined;
}) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt=""
        className={cn('shrink-0 rounded-full bg-white object-contain', AVATAR_CIRCLE[size])}
      />
    );
  }
  return <Avatar name={name} size={size} fetchCustom={false} decorative />;
}

function PreviewHeader({
  platform,
  name,
  handle,
  avatarUrl,
}: {
  platform: PreviewPlatform;
  name: string;
  handle: string | null;
  avatarUrl: string | null;
}) {
  const { timeLabel } = PREVIEW_STYLE[platform];
  const small = platform === SocialPlatform.INSTAGRAM || platform === SocialPlatform.THREADS;
  return (
    <div className={cn('flex items-center gap-2.5', small ? 'px-3 py-2.5' : 'px-4 py-3')}>
      <PreviewAvatar name={name} size={small ? 'sm' : 'md'} avatarUrl={avatarUrl} />
      <div className="min-w-0 flex-1 text-sm leading-tight">
        {platform === SocialPlatform.LINKEDIN || platform === SocialPlatform.FACEBOOK ? (
          <>
            <p className="truncate font-semibold text-ink">{name}</p>
            <p className="mt-0.5 flex items-center gap-1 text-xs text-ink-muted">
              {timeLabel}
              <span aria-hidden="true">·</span>
              <Globe className="h-3 w-3" aria-hidden="true" />
            </p>
          </>
        ) : (
          <p className="flex min-w-0 items-baseline gap-1">
            <span className="truncate font-semibold text-ink">{name}</span>
            {handle && <span className="truncate text-ink-muted">{handle}</span>}
            {platform !== SocialPlatform.INSTAGRAM && (
              <span className="shrink-0 text-ink-muted">
                {platform === SocialPlatform.X && <span aria-hidden="true">· </span>}
                {timeLabel}
              </span>
            )}
          </p>
        )}
      </div>
      <Ellipsis className="h-5 w-5 shrink-0 text-ink-faint" aria-hidden="true" />
    </div>
  );
}

/** The text, with any #hashtag inside it in the platform's link colour. */
export function PostText({ text, linkClass }: { text: string; linkClass: string }) {
  return (
    <>
      {hashtagRuns(text).map((run, i) =>
        run.hashtag ? (
          <span key={i} className={linkClass}>
            {run.text}
          </span>
        ) : (
          <Fragment key={i}>{run.text}</Fragment>
        ),
      )}
    </>
  );
}

/**
 * "…see more" / "show less". Sized like the inline text around it; the after-element stretches
 * the target to at least 44px each way without pushing the lines apart.
 */
function PreviewToggle({
  buttonRef,
  expanded,
  onClick,
  children,
}: {
  buttonRef?: Ref<HTMLButtonElement>;
  expanded: boolean;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  children: string;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      aria-expanded={expanded}
      onClick={onClick}
      className="focus-ring relative rounded font-medium text-ink-muted after:absolute after:-inset-x-2 after:-inset-y-3 after:content-[''] hover:text-ink-strong hover:underline"
    >
      {children}
    </button>
  );
}

/**
 * An unlabelled action icon. A size smaller, with tighter gaps around it, on the narrowest cards (the
 * composer's side column on a 1024px screen), so a row of five or six still fits; never squashed.
 */
const ROW_ICON = 'h-4 w-4 shrink-0 [@container(min-width:12rem)]:h-5 [@container(min-width:12rem)]:w-5';

/**
 * A platform's row of actions, across the card. `inset` is the same row inside a post's own content
 * column instead - a thread post's, beside its avatar: the column has already set it in from the
 * card's edge, so it has no side padding of its own, and on X the trailing pair (Bookmark, Share)
 * is drawn only where the column has room for all six icons.
 */
function ActionRow({ platform, inset = false }: { platform: PreviewPlatform; inset?: boolean }) {
  const { lead, trail, labelClass } = ACTIONS[platform];
  const isX = platform === SocialPlatform.X;
  // Each row's classes in three parts - layout, side padding, the rest - because the side padding
  // is the one part `inset` leaves out, and `cn` only joins: a class cannot be overridden later.
  const [layout, side, rest] = labelClass
    ? ['justify-around gap-y-1 border-t border-line-soft', 'px-2', 'py-1']
    : isX
      ? ['justify-between gap-2', 'px-2', 'py-2.5']
      : ['gap-2', 'px-3', 'py-2.5 [@container(min-width:12rem)]:gap-4'];
  return (
    <div
      aria-hidden="true"
      className={cn(
        // Wraps only on a card too narrow even for the icons (a very large text size in a narrow
        // column) - never cut off.
        'flex flex-wrap items-center text-ink-muted',
        layout,
        !inset && side,
        rest,
      )}
    >
      {lead.map(({ Icon, label }, i) =>
        label ? (
          <span key={label} className="flex items-center gap-1.5 px-1.5 py-2 text-xs font-semibold">
            <Icon className="h-4 w-4 shrink-0" />
            <span className={labelClass}>{label}</span>
          </span>
        ) : (
          <Icon key={i} className={ROW_ICON} />
        ),
      )}
      {trail && (
        <span
          className={cn(
            // Six small icons and the gaps between them need 8.5rem. In a narrower content column
            // X's trailing pair is left out: four icons on one line rather than six on two.
            inset && isX ? 'hidden [@container(min-width:8.5rem)]:flex' : 'flex',
            'items-center gap-2 [@container(min-width:12rem)]:gap-4',
            !isX && 'ml-auto',
          )}
        >
          {trail.map(({ Icon }, i) => (
            <Icon key={i} className={ROW_ICON} />
          ))}
        </span>
      )}
    </div>
  );
}

// Under another name outside this file: the app already has an `ActionRow`
// (features/approval/ApprovalActionBar.tsx).
export { ActionRow as PreviewActionRow };
